import { useState, useEffect, useRef, useCallback } from 'react';
import { signInEmail, signUpEmail, resetPassword, IS_CONFIGURED, supabase, db } from '../lib/supabase';

// ── Input sanitization & validation ──────────────────────────────────────────
function sanitizeInput(str) {
  if (typeof str !== 'string') return '';
  return str
    .replace(/[<>]/g, '')           // strip < > (XSS)
    .replace(/javascript:/gi, '')   // strip JS protocol
    .replace(/on\w+\s*=/gi, '')     // strip event handlers
    .trim()
    .slice(0, 500);                 // max length
}
function isValidEmail(email) {
  return /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(email);
}
// Block known disposable email domains (partial list — most common)
const DISPOSABLE_DOMAINS = ['mailinator.com','guerrillamail.com','temp-mail.org','throwaway.email',
  'yopmail.com','sharklasers.com','guerrillamailblock.com','grr.la','guerrillamail.info',
  'spam4.me','trashmail.com','trashmail.me','dispostable.com','maildrop.cc','tempmail.com',
  'getnada.com','discard.email','fakeinbox.com','mailnull.com','spamgourmet.com','tempr.email',
  'throwam.com','spamfree24.org','mailexpire.com','mt2015.com','filzmail.com'];
function isDisposableEmail(email) {
  const domain = (email || '').split('@')[1]?.toLowerCase() || '';
  return DISPOSABLE_DOMAINS.includes(domain);
}

// ── Client-side rate limiter (in-memory, per session) ─────────────────────────
const _rateLimits = {};
function rateLimit(key, maxAttempts, windowMs) {
  const now = Date.now();
  if (!_rateLimits[key]) _rateLimits[key] = [];
  _rateLimits[key] = _rateLimits[key].filter(t => now - t < windowMs);
  if (_rateLimits[key].length >= maxAttempts) {
    const oldest = _rateLimits[key][0];
    const retryIn = Math.ceil((oldest + windowMs - now) / 1000);
    return { blocked: true, retryIn };
  }
  _rateLimits[key].push(now);
  return { blocked: false };
}

// ── Password policy ───────────────────────────────────────────────────────────
function validatePassword(pw) {
  const rules = [
    { ok: pw.length >= 8,           label: '8+ characters' },
    { ok: /[A-Z]/.test(pw),         label: 'Uppercase letter' },
    { ok: /[a-z]/.test(pw),         label: 'Lowercase letter' },
    { ok: /[0-9]/.test(pw),         label: 'Number' },
    { ok: /[^A-Za-z0-9]/.test(pw),  label: 'Special character (!@#$...)' },
  ];
  return rules;
}

// ── Trusted device helpers ────────────────────────────────────────────────────
const LOGIN_TRUST_KEY = 'aspreo_trusted_login';
function getTrustedDevice() {
  try {
    const raw = localStorage.getItem(LOGIN_TRUST_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (d.expires && Date.now() > d.expires) { localStorage.removeItem(LOGIN_TRUST_KEY); return null; }
    return d;
  } catch { return null; }
}
function setTrustedDevice(userId) {
  const expires = Date.now() + 30 * 24 * 60 * 60 * 1000; // 30 days
  const token = `lt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem(LOGIN_TRUST_KEY, JSON.stringify({ userId, token, expires }));
}
function clearTrustedDevice() { localStorage.removeItem(LOGIN_TRUST_KEY); }

export default function AuthPage({ onSubscribe, modal }) {
  const [mode,       setMode]       = useState('login');
  const [form,       setForm]       = useState({ email: '', password: '', name: '', confirm: '' });
  const [err,        setErr]        = useState('');
  const [ok,         setOk]         = useState('');
  const [loading,    setLoading]    = useState(false);
  const [showPw,     setShowPw]     = useState(false);
  const [pwRules,    setPwRules]    = useState([]);
  const [otpSent,    setOtpSent]    = useState(false);
  const [otpCode,    setOtpCode]    = useState('');
  const [otpEmail,   setOtpEmail]   = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [trustDevice,setTrustDevice]= useState(false);
  const [loginMethod,setLoginMethod]= useState('password'); // 'password' | 'otp'
  const [otpCooldown,setOtpCooldown]= useState(0);
  const otpTimerRef = useRef(null);

  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Live password rules feedback
  useEffect(() => {
    if (mode === 'signup' && form.password) setPwRules(validatePassword(form.password));
    else setPwRules([]);
  }, [form.password, mode]);

  // OTP cooldown countdown
  useEffect(() => {
    if (otpCooldown > 0) {
      otpTimerRef.current = setTimeout(() => setOtpCooldown(c => c - 1), 1000);
    }
    return () => clearTimeout(otpTimerRef.current);
  }, [otpCooldown]);

  // ── Send Magic Link ─────────────────────────────────────────────────────────
  const sendMagicLink = async () => {
    if (!form.email.trim()) { setErr('Enter your email first.'); return; }
    const emailClean = sanitizeInput(form.email.trim().toLowerCase());
    if (!isValidEmail(emailClean)) { setErr('Enter a valid email address.'); return; }

    // Rate limit per email: max 3 magic links per 5 mins
    const rl = rateLimit(`magic_${emailClean}`, 3, 5 * 60 * 1000);
    if (rl.blocked) { setErr(`⏳ Too many requests. Try again in ${rl.retryIn}s.`); return; }

    setOtpLoading(true); setErr('');
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: emailClean,
        options: { shouldCreateUser: false },
      });
      // Always show same message — user enumeration protection
      setOtpSent(true);
      setOtpEmail(emailClean);
      setOtpCooldown(60);
    } catch (e) {
      setErr('❌ Could not send link. Try again shortly.');
    }
    setOtpLoading(false);
  };

  // ── Password login / signup ─────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(''); setOk('');

    // Rate limit: max 5 login attempts per 15 mins
    const rl = rateLimit(`login_${form.email}`, 5, 15 * 60 * 1000);
    if (rl.blocked && mode === 'login') {
      setErr(`⏳ Too many login attempts. Try again in ${rl.retryIn}s.`);
      return;
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        const identifier = sanitizeInput(form.email.trim());
        const isUsername = identifier.startsWith('@') || (!identifier.includes('@') && identifier.length > 0);
        let loginResult;
        if (isUsername) {
          const username = identifier.replace(/^@/, '').toLowerCase();
          loginResult = await db.signInWithUsername(username, form.password);
        } else {
          loginResult = await signInEmail(identifier.toLowerCase(), form.password);
        }
        if (loginResult.error) throw loginResult.error;

      } else if (mode === 'signup') {
        // Input validation & sanitization
        const cleanName  = sanitizeInput(form.name).trim();
        const cleanEmail = sanitizeInput(form.email).trim().toLowerCase();
        if (!cleanName || cleanName.length < 2) throw new Error('Full name is required (min 2 characters).');
        if (!isValidEmail(cleanEmail)) throw new Error('Enter a valid email address.');
        if (isDisposableEmail(cleanEmail)) throw new Error('Disposable/temporary email addresses are not allowed.');
        if (form.password !== form.confirm) throw new Error('Passwords do not match.');
        const rules = validatePassword(form.password);
        const failed = rules.filter(r => !r.ok);
        if (failed.length > 0) throw new Error(`Password needs: ${failed.map(r => r.label).join(', ')}`);

        const { error } = await signUpEmail(cleanEmail, form.password, { full_name: cleanName });
        if (error) throw error;
        setOk('✅ Account created! Check your email to confirm, then log in.');
        setMode('login');

      } else { // forgot
        const { error } = await resetPassword(form.email);
        if (error) throw error;
        // Generic message — don't reveal if email exists (user enumeration protection)
        setOk('✅ If an account exists for this email, a reset link was sent.');
      }
    } catch (e) {
      const msg = e.message || '';
      // Sanitized error messages — no internal details leaked
      if (msg.toLowerCase().includes('fetch') || msg.toLowerCase().includes('network')) {
        setErr('❌ Network error. Please check your connection and try again.');
      } else if (msg.includes('Invalid login credentials') || msg.includes('invalid_credentials')) {
        setErr('❌ Invalid email or password.'); // generic — no "email not found" hint
      } else if (msg.includes('Email not confirmed')) {
        setErr('❌ Please confirm your email first. Check your inbox.');
      } else if (msg.includes('User already registered')) {
        setErr('❌ An account with this email already exists.');
      } else if (msg.includes('Password')) {
        setErr('❌ ' + msg);
      } else {
        setErr('❌ Something went wrong. Please try again.'); // no raw error leakage
      }
    }
    setLoading(false);
  };

  const allPwOk = pwRules.length > 0 && pwRules.every(r => r.ok);

  return (
    <div style={{ ...(modal ? {} : { minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'linear-gradient(135deg,#0f172a 0%,#1a1040 50%,#0f172a 100%)' }), padding:16, fontFamily:"'Inter',sans-serif" }}>
      {!modal && (
        <div style={{ position:'fixed', inset:0, overflow:'hidden', pointerEvents:'none' }}>
          <div style={{ position:'absolute', top:'20%', left:'15%', width:400, height:400, borderRadius:'50%', background:'rgba(99,102,241,.08)', filter:'blur(80px)' }}/>
          <div style={{ position:'absolute', bottom:'20%', right:'15%', width:300, height:300, borderRadius:'50%', background:'rgba(139,92,246,.08)', filter:'blur(60px)' }}/>
        </div>
      )}

      <div style={{ width:'100%', maxWidth:480, position:'relative', zIndex:1 }}>
        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:24 }}>
          <div style={{ width:60, height:60, borderRadius:16, background:'linear-gradient(135deg,#6366f1,#8b5cf6)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:28, margin:'0 auto 12px', boxShadow:'0 8px 28px rgba(99,102,241,.4)' }}>📖</div>
          <h1 style={{ margin:'0 0 4px', fontSize:24, fontWeight:800, background:'linear-gradient(135deg,#6366f1,#8b5cf6)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>Aspreo Life</h1>
          <p style={{ margin:0, color:'#64748b', fontSize:13 }}>
            {mode==='login' ? 'Welcome back' : mode==='signup' ? 'Start your productivity journey' : 'Reset your password'}
          </p>
        </div>

        <div style={{ background:'rgba(30,41,59,.95)', border:'1px solid rgba(255,255,255,.08)', borderRadius:20, padding:28, boxShadow:'0 24px 80px rgba(0,0,0,.5)' }}>

          {/* ── Login method toggle ── */}
          {mode === 'login' && (
            <div style={{ display:'flex', gap:0, marginBottom:18, background:'rgba(0,0,0,.25)', borderRadius:10, padding:3 }}>
              {[['password','🔑 Password'],['magic','✉️ Magic Link']].map(([id,label]) => (
                <button key={id} onClick={() => { setLoginMethod(id); setErr(''); setOk(''); setOtpSent(false); }}
                  style={{ flex:1, padding:'7px', borderRadius:8, border:'none', background:loginMethod===id?'rgba(99,102,241,.3)':'transparent', color:loginMethod===id?'#a5b4fc':'#64748b', fontWeight:loginMethod===id?700:400, cursor:'pointer', fontSize:12, fontFamily:"'Inter',sans-serif", transition:'all .15s' }}>
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* ── Magic Link flow ── */}
          {mode === 'login' && loginMethod === 'magic' && (
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <AuthField label="Email Address" type="email" value={form.email} onChange={e => upd('email', e.target.value)} placeholder="you@example.com"/>
              {!otpSent ? (
                <button onClick={sendMagicLink} disabled={otpLoading || !form.email.trim()}
                  style={{ padding:'12px', borderRadius:10, border:'none', background:otpLoading||!form.email.trim()?'#334155':'linear-gradient(135deg,#6366f1,#8b5cf6)', color:'#fff', fontFamily:"'Inter',sans-serif", fontWeight:700, fontSize:15, cursor:otpLoading||!form.email.trim()?'not-allowed':'pointer' }}>
                  {otpLoading ? '⏳ Sending...' : '📧 Send Magic Link'}
                </button>
              ) : (
                <div style={{ background:'rgba(16,185,129,.08)', border:'1px solid rgba(16,185,129,.25)', borderRadius:10, padding:'16px', textAlign:'center' }}>
                  <div style={{ fontSize:28, marginBottom:8 }}>📬</div>
                  <div style={{ color:'#34d399', fontWeight:700, fontSize:14, marginBottom:6 }}>Magic link sent!</div>
                  <div style={{ color:'#94a3b8', fontSize:12, lineHeight:1.6 }}>
                    Check your inbox at <strong style={{ color:'#e2e8f0' }}>{otpEmail}</strong>.<br/>
                    Click the link in the email to sign in instantly.<br/>
                    <span style={{ fontSize:11, color:'#64748b' }}>Link expires in 10 minutes.</span>
                  </div>
                  <div style={{ marginTop:12, display:'flex', gap:8, justifyContent:'center' }}>
                    {otpCooldown > 0 ? (
                      <span style={{ color:'#475569', fontSize:12 }}>Resend in {otpCooldown}s</span>
                    ) : (
                      <button onClick={sendMagicLink} disabled={otpLoading}
                        style={{ background:'none', border:'1px solid rgba(99,102,241,.3)', color:'#6366f1', cursor:'pointer', fontSize:12, fontFamily:"'Inter',sans-serif", borderRadius:6, padding:'5px 12px' }}>
                        ↻ Resend
                      </button>
                    )}
                    <button onClick={() => { setOtpSent(false); setErr(''); setOk(''); }}
                      style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer', fontSize:12, fontFamily:"'Inter',sans-serif" }}>
                      ← Different email
                    </button>
                  </div>
                </div>
              )}
              {err && <ErrorBox msg={err}/>}
            </div>
          )}

          {/* ── Password flow ── */}
          {(mode !== 'login' || loginMethod === 'password') && (
            <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {mode === 'signup' && <AuthField label="Full Name" value={form.name} onChange={e => upd('name', e.target.value)} placeholder="Your full name"/>}
              <div>
                <label style={labelStyle}>Email or Username</label>
                <input value={form.email} onChange={e => upd('email', e.target.value)}
                  placeholder="you@example.com or @username"
                  required style={fieldStyle}
                  autoComplete="username"
                  onFocus={e=>{ e.target.style.borderColor='#6366f1'; e.target.style.boxShadow='0 0 0 3px rgba(99,102,241,.15)'; }}
                  onBlur={e=>{ e.target.style.borderColor='rgba(255,255,255,.1)'; e.target.style.boxShadow='none'; }}
                />
              </div>

              {mode !== 'forgot' && (
                <div>
                  <label style={labelStyle}>Password</label>
                  <div style={{ position:'relative' }}>
                    <input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => upd('password', e.target.value)}
                      placeholder={mode==='signup' ? 'Min. 8 chars, 1 upper, 1 number, 1 special' : 'Your password'}
                      required style={{ ...fieldStyle, paddingRight:40 }}
                      autoComplete={mode==='signup' ? 'new-password' : 'current-password'}
                      onFocus={e=>{ e.target.style.borderColor='#6366f1'; e.target.style.boxShadow='0 0 0 3px rgba(99,102,241,.15)'; }}
                      onBlur={e=>{ e.target.style.borderColor='rgba(255,255,255,.1)'; e.target.style.boxShadow='none'; }}
                    />
                    <button type="button" onClick={() => setShowPw(v => !v)}
                      style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#475569', fontSize:14 }}>
                      {showPw ? '🙈' : '👁'}
                    </button>
                  </div>
                  {/* Password strength rules — only on signup */}
                  {mode === 'signup' && pwRules.length > 0 && (
                    <div style={{ marginTop:8, display:'flex', flexWrap:'wrap', gap:5 }}>
                      {pwRules.map(r => (
                        <span key={r.label} style={{ fontSize:10, padding:'2px 8px', borderRadius:10, background:r.ok?'rgba(16,185,129,.15)':'rgba(255,255,255,.05)', color:r.ok?'#34d399':'#64748b', border:`1px solid ${r.ok?'rgba(16,185,129,.3)':'rgba(255,255,255,.08)'}`, transition:'all .2s' }}>
                          {r.ok ? '✓' : '○'} {r.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {mode === 'signup' && (
                <AuthField label="Confirm Password" type="password" value={form.confirm} onChange={e => upd('confirm', e.target.value)} placeholder="Repeat password" required/>
              )}

              {/* Trusted device toggle moved to MFA page only */}

              {err && <ErrorBox msg={err}/>}
              {ok  && <OkBox msg={ok}/>}

              <button type="submit" disabled={loading || (mode==='signup' && !allPwOk && form.password.length > 0)}
                style={{ padding:'12px', borderRadius:10, border:'none', background:loading?'#334155':'linear-gradient(135deg,#6366f1,#8b5cf6)', color:'#fff', fontFamily:"'Inter',sans-serif", fontWeight:700, fontSize:15, cursor:loading?'not-allowed':'pointer', boxShadow:loading?'none':'0 4px 16px rgba(99,102,241,.35)', transition:'all .2s', marginTop:4 }}>
                {loading ? '⏳ Please wait...' : mode==='login' ? '→ Sign In' : mode==='signup' ? '🚀 Create Account' : '📧 Send Reset Email'}
              </button>
            </form>
          )}

          {/* ── Nav links ── */}
          {(mode !== 'login' || loginMethod === 'password') && (
            <div style={{ marginTop:18, textAlign:'center', display:'flex', flexDirection:'column', gap:8 }}>
              {mode === 'login' && <>
                <button onClick={() => setMode('forgot')} style={lnk}>Forgot password?</button>
                <div style={{ color:'#64748b', fontSize:13 }}>No account? <button onClick={() => setMode('signup')} style={{...lnk,display:'inline'}}>Create one free →</button></div>
              </>}
              {mode === 'signup' && <div style={{ color:'#64748b', fontSize:13 }}>Already have an account? <button onClick={() => setMode('login')} style={{...lnk,display:'inline'}}>Sign in →</button></div>}
              {mode === 'forgot' && <button onClick={() => setMode('login')} style={lnk}>← Back to sign in</button>}
            </div>
          )}
          {mode === 'login' && loginMethod === 'magic' && (
            <div style={{ marginTop:14, textAlign:'center' }}>
              <div style={{ color:'#64748b', fontSize:13 }}>No account? <button onClick={() => setMode('signup')} style={{...lnk,display:'inline'}}>Create one free →</button></div>
            </div>
          )}

          {mode === 'signup' && (
            <div style={{ marginTop:16, paddingTop:14, borderTop:'1px solid rgba(255,255,255,.07)', textAlign:'center' }}>
              <button onClick={onSubscribe} style={{ color:'#6366f1', background:'none', border:'none', cursor:'pointer', fontSize:12, fontFamily:"'Inter',sans-serif" }}>
                📬 Subscribe to Aspreo Life newsletter
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TrustDeviceToggle({ value, onChange }) {
  return (
    <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'8px 0' }}>
      <div onClick={() => onChange(!value)} style={{ width:36, height:20, borderRadius:10, background:value?'#6366f1':'#334155', position:'relative', transition:'background .2s', flexShrink:0 }}>
        <div style={{ position:'absolute', top:3, left:value?18:3, width:14, height:14, borderRadius:'50%', background:'#fff', transition:'left .2s', boxShadow:'0 1px 3px rgba(0,0,0,.3)' }}/>
      </div>
      <span style={{ fontSize:12, color:'#64748b', userSelect:'none' }}>Trust this device for 30 days</span>
    </label>
  );
}

function AuthField({ label, value, onChange, placeholder, type='text', required }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} required={required}
        autoComplete={type==='password'?'current-password':'off'}
        style={fieldStyle}
        onFocus={e=>{ e.target.style.borderColor='#6366f1'; e.target.style.boxShadow='0 0 0 3px rgba(99,102,241,.15)'; }}
        onBlur={e=>{ e.target.style.borderColor='rgba(255,255,255,.1)'; e.target.style.boxShadow='none'; }}
      />
    </div>
  );
}

function ErrorBox({ msg }) {
  return <div style={{ background:'rgba(244,63,94,.1)', border:'1px solid rgba(244,63,94,.25)', color:'#f87171', padding:'10px 14px', borderRadius:8, fontSize:13, lineHeight:1.5 }}>{msg}</div>;
}
function OkBox({ msg }) {
  return <div style={{ background:'rgba(16,185,129,.1)', border:'1px solid rgba(16,185,129,.25)', color:'#34d399', padding:'10px 14px', borderRadius:8, fontSize:13 }}>{msg}</div>;
}

const labelStyle = { display:'block', fontSize:11, fontWeight:600, color:'#64748b', marginBottom:5, textTransform:'uppercase', letterSpacing:'.05em' };
const fieldStyle  = { width:'100%', padding:'10px 14px', background:'#0f172a', border:'1px solid rgba(255,255,255,.1)', borderRadius:8, color:'#f1f5f9', WebkitTextFillColor:'#f1f5f9', fontFamily:"'Inter',sans-serif", fontSize:14, outline:'none', boxSizing:'border-box', transition:'all .18s', WebkitBoxShadow:'0 0 0 1000px #0f172a inset', caretColor:'#f1f5f9' };
const lnk = { background:'none', border:'none', color:'#6366f1', cursor:'pointer', fontSize:13, fontFamily:"'Inter',sans-serif", fontWeight:500 };

export { getTrustedDevice, setTrustedDevice, clearTrustedDevice };
